/**
 * Money display for the super-admin plane — the tenant profile's two figures, and the
 * plan price grid PlansManager edits.
 *
 * Pure on purpose: every mistake these functions exist to prevent is a DISPLAY mistake
 * (a figure that does not name whose money it is, a price printed for a currency that
 * has none), and a display mistake is only testable when the formatting is separable
 * from the component. `@/`-aliased imports are also unresolvable under Jest, so a
 * component module cannot be unit-tested at all — one more reason these live here.
 */

import { OPERATING_CURRENCIES } from "../lib/currencies";

/**
 * `[{currency, amount}]` (as `/360` returns per money) → display strings.
 *
 * Each amount carries the currency of the ROWS it came from, never the tenant's
 * currency: a practice billed in USD whose clients pay in CAD gets two lines, not one
 * sum labelled USD. An empty list means "no captured transaction at all" — the caller
 * decides what to print for it, because "nothing happened" is not "0.00".
 */
export function formatMoneyLines(lines) {
  return (lines || []).map((l) => `${Number(l.amount || 0).toFixed(2)} ${l.currency}`);
}

/**
 * Parses one currency's raw input string — monthly AND annual alike.
 * Blank means "not sold in this currency": the key is OMITTED from the POST/PUT body
 * entirely, never sent as an explicit 0. `_validate_price_map` (super_admin_routes.py:
 * 323-354) only validates keys that are present, and the public pricing page must not
 * advertise a $0 card for a currency the plan isn't sold in (PricingPlans.jsx). The one
 * invariant the backend still enforces is that at least one currency resolves to a
 * strictly positive monthly amount (`_require_a_sellable_price`,
 * super_admin_routes.py:357-368) — mirrored client-side by `hasPositiveMonthly` below.
 */
export function parsePrice(raw) {
  const s = String(raw ?? "").trim();
  if (s === "") return { ok: true, value: undefined };
  const n = Number(s);
  if (!Number.isFinite(n) || n < 0) return { ok: false };
  return { ok: true, value: n };
}

/**
 * Which currencies this draft is publicly LISTED in, and which it is hidden in.
 *
 * "Active" is not "visible everywhere", and nothing on this screen said so. A plan with
 * a CAD price only is served by /api/saas/plans (verified: `active: true`, present in
 * the payload) but its card is absent from the public grid under USD — correctly, since
 * signup answers 400 "Plan not available in USD." for it. The operator who ticked
 * "active" and then could not find the plan on the pricing page had no way to learn
 * that from here; the rule is the SAME predicate the public grid applies
 * (PricingPlans.jsx `isSoldIn`) and the same one `resolve_plan_amount` charges on:
 * a strictly positive MONTHLY price in that currency.
 */
export const publicCurrencies = (d) => {
  const shown = OPERATING_CURRENCIES.filter((c) => (parsePrice(d.monthly[c]).value || 0) > 0);
  return { shown, hidden: OPERATING_CURRENCIES.filter((c) => !shown.includes(c)) };
};

/** The backend's one hard rule (`_require_a_sellable_price`): at least one currency.
 *  Derived from `publicCurrencies` rather than re-scanning the grid — "sellable at all"
 *  and "sellable in THIS currency" must never be able to disagree. */
export const hasPositiveMonthly = (d) => publicCurrencies(d).shown.length > 0;

